import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * The viewer's own fixes to a playlist's guide (Phase 3 step 4,
 * docs/build-guide-playlists-epg.md §5): "Choose guide" pins and channel time
 * shifts. Kept on this TV, per playlist, by channel name as the guide matches
 * it (lower-cased, trimmed): every listing of one channel, in Sports, in USA
 * or in Favourites, is the same channel to the guide, so it takes the same pin.
 */

/** A pinned guide channel: from the playlist's own guide, or one of its extra sources. */
export interface GuidePin {
  /** "own", or the extra source's id (GuideFeedConfig.id), so a move or an edit keeps it. */
  feed: string;
  guideChannelId: string;
  /** The guide channel's own name, to show what it is pinned to. */
  guideName?: string;
}

/** Within 12 hours either way, in whole minutes. */
export const MAX_SHIFT_MINUTES = 720;
/** The step the shift settings move by. */
export const SHIFT_STEP_MINUTES = 15;

/** A shift as the settings save it: whole minutes within MAX_SHIFT_MINUTES. */
export function clampShift(minutes: number): number {
  if (!Number.isFinite(minutes)) return 0;
  return Math.max(-MAX_SHIFT_MINUTES, Math.min(MAX_SHIFT_MINUTES, Math.round(minutes)));
}

/** "+1:30", "−0:45", "0:00": a shift as the settings show it. */
export function shiftLabel(minutes: number): string {
  const sign = minutes > 0 ? "+" : minutes < 0 ? "−" : "";
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, "0")}`;
}

/** A channel's name as the guide matches it (epg/hooks.ts matchNames). */
export function guideNameKey(name: string): string {
  return name.toLowerCase().trim();
}

interface GuideMapState {
  /** sourceId → channel name → pin. */
  pins: Record<string, Record<string, GuidePin>>;
  /** sourceId → channel name → minutes; only the viewer's own, the playlist's tvg-shift is not copied here. */
  shifts: Record<string, Record<string, number>>;
  setPin: (sourceId: string, channelName: string, pin: GuidePin | null) => void;
  /** null goes back to the playlist's or the source's shift. */
  setShift: (sourceId: string, channelName: string, minutes: number | null) => void;
}

function withEntry<T>(
  all: Record<string, Record<string, T>>,
  sourceId: string,
  key: string,
  value: T | null,
): Record<string, Record<string, T>> {
  const own = { ...(all[sourceId] ?? {}) };
  if (value === null) delete own[key];
  else own[key] = value;
  const next = { ...all };
  if (Object.keys(own).length === 0) delete next[sourceId];
  else next[sourceId] = own;
  return next;
}

export const useGuideMap = create<GuideMapState>()(
  persist(
    (set) => ({
      pins: {},
      shifts: {},
      setPin: (sourceId, channelName, pin) => {
        const key = guideNameKey(channelName);
        if (!key) return;
        set((state) => ({ pins: withEntry(state.pins, sourceId, key, pin) }));
      },
      setShift: (sourceId, channelName, minutes) => {
        const key = guideNameKey(channelName);
        if (!key) return;
        set((state) => ({ shifts: withEntry(state.shifts, sourceId, key, minutes === null ? null : clampShift(minutes)) }));
      },
    }),
    { name: "slique-guide-map", version: 1, partialize: (state) => ({ pins: state.pins, shifts: state.shifts }) },
  ),
);
